import { useEffect, useMemo, useState, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  Search,
  ArrowDownUp,
  Download,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  ShieldCheck,
  RefreshCw,
  CheckCircle2,
  Clock,
  PauseCircle,
  Send,
  X,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Eye,
  MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { authHeaders, formatApiErrorDetail } from "@/lib/auth";

const ROLE_LABELS = {
  adult_child: "Adult Child",
  senior: "Senior",
  caregiver: "Caregiver",
  other: "Other",
};

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "waitlisted", label: "Waitlisted" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pending", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
    approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
    waitlisted: { label: "Waitlisted", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: PauseCircle },
  };
  const m = map[status] || map.pending;
  const Icon = m.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function BetaSignupsPanel() {
  const [rows, setRows] = useState([]);
  const [activeApk, setActiveApk] = useState(null);
  const [allApks, setAllApks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortDesc, setSortDesc] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' | 'waitlist' | 'resend'
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [signupsRes, apksRes, activeRes] = await Promise.all([
        api.get("/admin/beta-signups", { headers: authHeaders() }),
        api.get("/admin/apks", { headers: authHeaders() }),
        api.get("/admin/apks/active", { headers: authHeaders() }),
      ]);
      setRows(signupsRes.data || []);
      setAllApks(apksRes.data || []);
      setActiveApk(activeRes.data || null);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(""), 3500);
    return () => clearTimeout(t);
  }, [success]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? rows.filter((r) =>
          [r.name, r.email, r.city, r.senior_location, r.phone, r.status, r.status_reason]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q)),
        )
      : rows;
    if (statusFilter !== "all") {
      list = list.filter((r) => (r.status || "pending") === statusFilter);
    }
    list = [...list].sort((a, b) => {
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      return sortDesc ? bt - at : at - bt;
    });
    return list;
  }, [rows, query, statusFilter, sortDesc]);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, approved: 0, waitlisted: 0 };
    for (const r of rows) {
      const s = r.status || "pending";
      if (c[s] !== undefined) c[s] += 1;
    }
    return c;
  }, [rows]);

  const exportCsv = () => {
    const header = [
      "Name",
      "Email",
      "Phone",
      "Role",
      "City",
      "Parent's Location",
      "Status",
      "Status Reason",
      "APK Sent",
      "Last Email At",
      "Share Clicks",
      "Shares",
      "Shared Phones",
      "Joined",
    ];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      const fields = [
        r.name,
        r.email,
        r.phone || "",
        ROLE_LABELS[r.role] || r.role,
        r.city || "",
        r.senior_location || "",
        r.status || "pending",
        r.status_reason || "",
        r.apk_version_sent || "",
        r.last_email_sent_at ? fmtDate(r.last_email_sent_at) : "",
        r.share_click_count || 0,
        r.share_count || 0,
        (r.shared_phones || []).join(" | "),
        fmtDate(r.created_at),
      ];
      lines.push(fields.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `careable-beta-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openAction = (row, type) => {
    setActionRow(row);
    setActionType(type);
  };

  const closeAction = () => {
    setActionRow(null);
    setActionType(null);
  };

  const handleConfirm = async ({ reason, apkId }) => {
    if (!actionRow || !actionType) return;
    setBusyId(actionRow.id);
    setError("");
    try {
      if (actionType === "approve") {
        await api.post(
          `/admin/beta-signups/${actionRow.id}/status`,
          { status: "approved", apk_id: apkId || null },
          { headers: authHeaders() },
        );
        setSuccess(`Approved ${actionRow.name}. Email scheduled.`);
      } else if (actionType === "waitlist") {
        await api.post(
          `/admin/beta-signups/${actionRow.id}/status`,
          { status: "waitlisted", reason: reason || "" },
          { headers: authHeaders() },
        );
        setSuccess(`Waitlisted ${actionRow.name}. Email scheduled.`);
      } else if (actionType === "resend") {
        await api.post(
          `/admin/beta-signups/${actionRow.id}/resend`,
          { apk_id: apkId || null },
          { headers: authHeaders() },
        );
        setSuccess(`Resent email to ${actionRow.name}.`);
      }
      closeAction();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="beta-panel"
      className="bg-white border border-[#C9DDD6] rounded-3xl overflow-hidden"
    >
      {/* Header */}
      <div className="p-5 border-b border-[#C9DDD6] flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-[#0E2A36]">Beta Signups</h2>
            <p className="text-xs text-[#3D5560] mt-0.5">
              Approve users (send APK) or waitlist them with a reason. Active APK:{" "}
              <span className="font-semibold text-[#1F4E5F]">
                {activeApk ? activeApk.version : "none uploaded yet"}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3D5560]/60" />
              <input
                data-testid="beta-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, status..."
                className="pl-9 pr-3 py-2 rounded-full bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm w-64"
              />
            </div>
            <button
              onClick={() => setSortDesc((v) => !v)}
              title={sortDesc ? "Newest first" : "Oldest first"}
              className="w-9 h-9 rounded-full bg-[#F4FAF8] border border-[#C9DDD6] flex items-center justify-center text-[#3D5560] hover:bg-[#E1EEEA]"
            >
              <ArrowDownUp className="w-4 h-4" />
            </button>
            <button
              onClick={load}
              title="Refresh"
              className="w-9 h-9 rounded-full bg-[#F4FAF8] border border-[#C9DDD6] flex items-center justify-center text-[#3D5560] hover:bg-[#E1EEEA]"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-1.5 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-3 py-2 rounded-full text-xs font-semibold"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>

        {/* Status filters */}
        <div className="inline-flex items-center gap-1 bg-[#F4FAF8] border border-[#C9DDD6] rounded-full p-1 w-fit">
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`beta-filter-${f.key}`}
                onClick={() => setStatusFilter(f.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? "bg-[#1F4E5F] text-[#F4FAF8]"
                    : "text-[#3D5560] hover:bg-[#E1EEEA]"
                }`}
              >
                {f.label}
                <span
                  className={`min-w-[1.25rem] text-center text-[10px] font-bold px-1.5 rounded-full ${
                    active ? "bg-white/15 text-[#F4FAF8]" : "bg-white text-[#3D5560]"
                  }`}
                >
                  {counts[f.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {!activeApk && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-2.5 text-xs flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Please upload an APK in the <strong>Beta APK</strong> tab before approving any signups.
          </div>
        )}
      </div>

      {/* Messages */}
      {(error || success) && (
        <div className="px-5 pt-4">
          {error && (
            <div
              data-testid="beta-error"
              className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-2 text-sm"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              data-testid="beta-success"
              className="rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-2 text-sm inline-flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" /> {success}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 flex items-center gap-2 text-[#3D5560]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading signups…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#3D5560] text-sm">
            No beta signups match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4FAF8] text-[#3D5560] text-xs uppercase tracking-wider">
                <th className="w-10 px-2 py-3"></th>
                <th className="text-left px-3 py-3 font-semibold">Name</th>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Joined</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const status = r.status || "pending";
                const expanded = expandedId === r.id;
                const isBusy = busyId === r.id;
                const toggle = () => setExpandedId(expanded ? null : r.id);
                return (
                  <Fragment key={r.id}>
                    <tr
                      data-testid={`beta-row-${r.id}`}
                      onClick={toggle}
                      className={`border-t border-[#E1EEEA] cursor-pointer transition-colors ${
                        expanded
                          ? "bg-[#F4FAF8]"
                          : "hover:bg-[#F4FAF8]/60"
                      }`}
                    >
                      <td className="px-2 py-4 align-middle">
                        <button
                          data-testid={`beta-expand-${r.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle();
                          }}
                          aria-label={expanded ? "Collapse details" : "Expand details"}
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                            expanded
                              ? "bg-[#1F4E5F] text-[#F4FAF8]"
                              : "bg-[#E1EEEA] text-[#1F4E5F] hover:bg-[#C9DDD6]"
                          }`}
                        >
                          {expanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-4">
                        <div className="font-semibold text-[#0E2A36] hover:text-[#1F4E5F] inline-flex items-center gap-1.5">
                          {r.name}
                        </div>
                        <div className="text-[11px] text-[#3D5560]/80 mt-0.5">
                          {ROLE_LABELS[r.role] || r.role}
                          {r.city ? ` · ${r.city}` : ""}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <a
                          href={`mailto:${r.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[#1F4E5F] hover:underline"
                        >
                          {r.email}
                        </a>
                      </td>
                      <td className="px-5 py-4">
                        <StatusPill status={status} />
                      </td>
                      <td className="px-5 py-4 text-[#3D5560] text-xs">
                        {fmtDate(r.created_at)}
                      </td>
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <ActionBtn
                            variant="ghost"
                            testId={`beta-view-${r.id}`}
                            onClick={toggle}
                            icon={Eye}
                          >
                            {expanded ? "Hide" : "View"}
                          </ActionBtn>
                          {status === "pending" && (
                            <>
                              <ActionBtn
                                variant="primary"
                                testId={`beta-approve-${r.id}`}
                                disabled={isBusy || !activeApk}
                                onClick={() => openAction(r, "approve")}
                                icon={CheckCircle2}
                              >
                                Approve
                              </ActionBtn>
                              <ActionBtn
                                variant="secondary"
                                testId={`beta-waitlist-${r.id}`}
                                disabled={isBusy}
                                onClick={() => openAction(r, "waitlist")}
                                icon={PauseCircle}
                              >
                                Waitlist
                              </ActionBtn>
                            </>
                          )}
                          {status === "approved" && (
                            <>
                              <ActionBtn
                                variant="ghost"
                                testId={`beta-resend-${r.id}`}
                                disabled={isBusy || !activeApk}
                                onClick={() => openAction(r, "resend")}
                                icon={Send}
                              >
                                Re-send APK
                              </ActionBtn>
                              <ActionBtn
                                variant="ghost"
                                testId={`beta-to-waitlist-${r.id}`}
                                disabled={isBusy}
                                onClick={() => openAction(r, "waitlist")}
                                icon={PauseCircle}
                              >
                                Move to waitlist
                              </ActionBtn>
                            </>
                          )}
                          {status === "waitlisted" && (
                            <>
                              <ActionBtn
                                variant="primary"
                                testId={`beta-approve-${r.id}`}
                                disabled={isBusy || !activeApk}
                                onClick={() => openAction(r, "approve")}
                                icon={CheckCircle2}
                              >
                                Approve
                              </ActionBtn>
                              <ActionBtn
                                variant="ghost"
                                testId={`beta-resend-${r.id}`}
                                disabled={isBusy}
                                onClick={() => openAction(r, "resend")}
                                icon={Send}
                              >
                                Re-send waitlist
                              </ActionBtn>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    <AnimatePresence initial={false}>
                      {expanded && (
                        <motion.tr
                          key={`${r.id}-details`}
                          data-testid={`beta-details-${r.id}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className="bg-[#F4FAF8]/70 border-t border-[#E1EEEA]"
                        >
                          <td colSpan={6} className="px-5 sm:px-8 py-5">
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.22 }}
                              className="overflow-hidden"
                            >
                              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-[13px] text-[#3D5560]">
                                <Detail icon={Mail} label="Email" value={r.email} />
                                <Detail icon={Phone} label="Phone" value={r.phone || "—"} />
                                <Detail
                                  icon={Users}
                                  label="Role"
                                  value={ROLE_LABELS[r.role] || r.role}
                                />
                                <Detail
                                  icon={MapPin}
                                  label="City"
                                  value={r.city || "—"}
                                />
                                <Detail
                                  icon={MapPin}
                                  label="Parent's State / UT"
                                  value={r.senior_location || "—"}
                                />
                                <Detail
                                  icon={Calendar}
                                  label="Submitted at"
                                  value={fmtDate(r.created_at)}
                                />
                                <Detail
                                  icon={ShieldCheck}
                                  label="Privacy consent"
                                  value={
                                    r.consent_privacy_notice
                                      ? `Accepted${r.consent_recorded_at ? ` · ${fmtDate(r.consent_recorded_at)}` : ""}`
                                      : "Not recorded"
                                  }
                                />
                                {r.status_updated_at && (
                                  <Detail
                                    icon={Clock}
                                    label="Status updated"
                                    value={`${fmtDate(r.status_updated_at)}${r.status_updated_by ? ` · by ${r.status_updated_by}` : ""}`}
                                  />
                                )}
                                {r.apk_version_sent && (
                                  <Detail
                                    icon={Send}
                                    label="APK sent"
                                    value={`${r.apk_version_sent}${r.last_email_sent_at ? ` · ${fmtDate(r.last_email_sent_at)}` : ""}`}
                                  />
                                )}
                                {r.download_token_expires_at && (
                                  <Detail
                                    icon={Clock}
                                    label="Download link expires"
                                    value={fmtDate(r.download_token_expires_at)}
                                  />
                                )}
                                <Detail
                                  icon={MessageCircle}
                                  label="Share with Parent"
                                  value={
                                    (r.share_click_count || 0) === 0 &&
                                    (r.share_count || 0) === 0
                                      ? "Not clicked yet"
                                      : `${r.share_click_count || 0} click${(r.share_click_count || 0) === 1 ? "" : "s"} · ${r.share_count || 0} share${(r.share_count || 0) === 1 ? "" : "s"}`
                                  }
                                />
                                {(r.shared_phones || []).length > 0 && (
                                  <Detail
                                    icon={Phone}
                                    label="Shared with"
                                    value={(r.shared_phones || []).join(", ")}
                                  />
                                )}
                              </div>
                              {r.status_reason && (
                                <div className="mt-4">
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#3D5560]/70 mb-1">
                                    {status === "waitlisted" ? "Waitlist reason" : "Note"}
                                  </p>
                                  <p className="text-[13px] text-[#0E2A36] whitespace-pre-line bg-white border border-[#C9DDD6] rounded-xl px-3 py-2.5 max-w-3xl">
                                    {r.status_reason}
                                  </p>
                                </div>
                              )}
                            </motion.div>
                          </td>
                        </motion.tr>
                      )}
                    </AnimatePresence>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Action modal */}
      <AnimatePresence>
        {actionRow && actionType && (
          <ActionDialog
            row={actionRow}
            type={actionType}
            apks={allApks}
            activeApkId={activeApk?.id}
            busy={busyId === actionRow.id}
            onClose={closeAction}
            onConfirm={handleConfirm}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="w-3.5 h-3.5 mt-1 text-[#3D5560]/60 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#3D5560]/70">
          {label}
        </p>
        <p className="text-[13px] text-[#0E2A36] truncate">{value}</p>
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, icon: Icon, variant = "primary", disabled, testId }) {
  const cls = {
    primary:
      "bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] disabled:opacity-50",
    secondary:
      "bg-[#E8A33D] hover:bg-[#C88A28] text-white disabled:opacity-50",
    ghost:
      "bg-[#F4FAF8] hover:bg-[#E1EEEA] text-[#1F4E5F] border border-[#C9DDD6] disabled:opacity-50",
  }[variant];
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${cls}`}
    >
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function ActionDialog({ row, type, apks, activeApkId, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState(type === "waitlist" ? "" : row.status_reason || "");
  const [apkId, setApkId] = useState(activeApkId || "");
  const requiresReason = type === "waitlist";
  const requiresApk = type === "approve" || type === "resend";

  const presetReasons = [
    "We've hit our capacity for this onboarding batch. You'll move up automatically in the next batch.",
    "Your senior's state/UT isn't yet covered by a Health Coordinator. We're expanding region by region.",
    "We're prioritising adult-child accounts in this wave; you'll be re-evaluated soon.",
  ];

  const titles = {
    approve: `Approve ${row.name}?`,
    waitlist: `Waitlist ${row.name}?`,
    resend:
      row.status === "approved"
        ? `Re-send APK to ${row.name}?`
        : `Re-send waitlist email to ${row.name}?`,
  };

  const ctas = {
    approve: "Approve & send APK",
    waitlist: "Waitlist & send email",
    resend: "Re-send email",
  };

  const descriptions = {
    approve:
      "A welcome email with a time-limited download link to the APK will be sent right away.",
    waitlist:
      "A polite waitlist email including the reason below will be sent. The reason will also appear in the user's profile.",
    resend:
      row.status === "approved"
        ? "A fresh download link will be generated and emailed. The previous link will stop working."
        : "The waitlist email will be resent using the reason already on file.",
  };

  const handle = () => {
    if (requiresReason && !reason.trim()) return;
    onConfirm({ reason: reason.trim(), apkId: requiresApk ? apkId : null });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.97 }}
        transition={{ duration: 0.18 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        data-testid="beta-action-dialog"
      >
        <div className="px-6 py-5 border-b border-[#E1EEEA] flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-[#0E2A36]">{titles[type]}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#E1EEEA] flex items-center justify-center text-[#3D5560]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#3D5560]">{descriptions[type]}</p>

          <div className="rounded-2xl border border-[#C9DDD6] bg-[#F4FAF8] px-4 py-3 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#1F4E5F]" />
              <span className="font-semibold text-[#0E2A36]">{row.email}</span>
            </div>
          </div>

          {requiresApk && (
            <div>
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                APK build to send *
              </label>
              <select
                data-testid="dialog-apk-select"
                value={apkId}
                onChange={(e) => setApkId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
              >
                <option value="">— Use active build —</option>
                {apks.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.version} ({Math.round((a.size_bytes || 0) / (1024 * 1024))} MB)
                    {a.is_active ? " · active" : ""}
                  </option>
                ))}
              </select>
              {apks.length === 0 && (
                <p className="mt-2 text-xs text-amber-700">
                  No APK builds uploaded yet. Add one in the "Beta APK" tab first.
                </p>
              )}
            </div>
          )}

          {requiresReason && (
            <div>
              <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
                Reason for waitlisting *
              </label>
              <textarea
                data-testid="dialog-reason-input"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. We've hit our capacity for this batch..."
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm leading-relaxed"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {presetReasons.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReason(p)}
                    className="text-[11px] text-[#1F4E5F] hover:text-[#173E50] bg-[#E1EEEA] hover:bg-[#C9DDD6] px-2.5 py-1 rounded-full"
                  >
                    Use preset {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-[#FAFCFB] border-t border-[#E1EEEA] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-semibold text-[#3D5560] hover:bg-[#E1EEEA]"
          >
            Cancel
          </button>
          <button
            data-testid="dialog-confirm"
            onClick={handle}
            disabled={busy || (requiresReason && !reason.trim())}
            className="inline-flex items-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] disabled:opacity-60 text-[#F4FAF8] px-5 py-2 rounded-full text-sm font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {ctas[type]}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
